const photoLocations = [
  { name: "CVS Pharmacy — Greenville", link: "https://www.cvs.com/store-locator/", note: "Multiple Greenville metro locations, walk-in service" },
  { name: "Walgreens — Simpsonville", link: "https://www.walgreens.com/storelocator", note: "Appointment recommended, walk-ins accepted" },
  { name: "The UPS Store — Greenville", link: "https://www.theupsstore.com/tools/find-a-store", note: "Photos + document overnight shipping in one stop" },
  { name: "FedEx Office — Greenville", link: "https://www.fedex.com/en-us/shipping/store/locator.html", note: "Overnight shipping and passport photos available" },
  { name: "Walmart — Mauldin", link: "https://www.walmart.com/store-finder", note: "Photo center at select locations" },
];

export default function GreenvilleSCHelpfulResourcesSection() {
  return (
    <section id="local-resources" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Local Resources</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Helpful Resources for Greenville Travelers
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            Getting your expedited passport ready involves more than just the application. Here are local Greenville resources to help you gather everything you need.
          </p>
        </div>

        <div className="space-y-6">
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="bg-[#1A365D] px-6 py-5 flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/15 flex-shrink-0">
                <i className="ri-camera-line text-white text-lg"></i>
              </div>
              <div>
                <p className="text-white/60 text-xs font-semibold uppercase tracking-widest">Passport Photos</p>
                <p className="text-white font-bold text-base leading-tight mt-0.5">Passport Photo Locations in Greenville &amp; the Upstate</p>
              </div>
            </div>
            <div className="px-6 py-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
                {photoLocations.map((loc, i) => (
                  <div key={i} className="bg-[#F7F8FA] border border-gray-200 rounded-lg p-3.5">
                    <a href={loc.link} target="_blank" rel="noopener noreferrer" className="font-bold text-[#1A365D] text-xs leading-snug mb-1 inline-flex items-center gap-1 hover:text-[#F6C344] transition-colors">
                      {loc.name} <i className="ri-external-link-line text-[10px]"></i>
                    </a>
                    <p className="text-gray-500 text-xs leading-relaxed">{loc.note}</p>
                  </div>
                ))}
              </div>
              <div className="bg-[#F7F8FA] border border-gray-200 rounded-md p-3">
                <p className="text-gray-600 text-xs leading-relaxed">
                  <i className="ri-lightbulb-line text-[#F6C344] mr-1.5"></i>
                  <strong>Tip:</strong> Wear a plain white or off-white top, no glasses, neutral expression. Photos must meet strict U.S. State Department specifications.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="bg-[#1A365D] px-6 py-5 flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/15 flex-shrink-0">
                <i className="ri-flight-takeoff-line text-white text-lg"></i>
              </div>
              <div>
                <p className="text-white/60 text-xs font-semibold uppercase tracking-widest">Airports</p>
                <p className="text-white font-bold text-base leading-tight mt-0.5">Airports Serving Greenville &amp; the Upstate</p>
              </div>
            </div>
            <div className="px-6 py-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
                {[
                  { code: "GSP", name: "Greenville-Spartanburg International Airport", address: "2000 GSP Dr, Greer, SC 29651", distance: "Primary airport — ~15 mi northeast of downtown Greenville", note: "Direct service to Atlanta, Charlotte, New York, Washington DC, and other major hubs. Growing international connections.", link: "https://www.gspairport.com/", featured: true },
                  { code: "CLT", name: "Charlotte Douglas International Airport", address: "5501 Josh Birmingham Pkwy, Charlotte, NC 28208", distance: "~1.5 hours north — broadest international options", note: "Major international hub with direct transatlantic flights. Many Greenville travelers use CLT for direct international departures.", link: "https://www.cltairport.com/", featured: false },
                ].map((airport, i) => (
                  <div key={i} className={`rounded-lg p-4 border ${airport.featured ? "bg-amber-50 border-[#F6C344]/40" : "bg-[#F7F8FA] border-gray-200"}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-[#1A365D] font-black text-xl leading-none">{airport.code}</span>
                      {airport.featured && <span className="text-xs font-bold text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">Primary</span>}
                    </div>
                    <p className="font-bold text-[#1A365D] text-xs leading-snug mb-1">{airport.name}</p>
                    <div className="flex items-center gap-1.5 text-xs text-[#F6C344] font-semibold mb-2">
                      <i className="ri-road-map-line"></i>
                      <span>{airport.distance}</span>
                    </div>
                    <p className="text-gray-500 text-xs leading-relaxed mb-1">{airport.address}</p>
                    <p className="text-gray-400 text-xs leading-relaxed mb-3">{airport.note}</p>
                    <a href={airport.link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-[#1A365D] hover:text-[#F6C344] font-semibold text-xs transition-colors">
                      <i className="ri-external-link-line text-sm"></i>Airport Website
                    </a>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="bg-[#1A365D] px-6 py-5 flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/15 flex-shrink-0">
                <i className="ri-file-list-3-line text-white text-lg"></i>
              </div>
              <div>
                <p className="text-white/60 text-xs font-semibold uppercase tracking-widest">Vital Records</p>
                <p className="text-white font-bold text-base leading-tight mt-0.5">South Carolina Birth Certificate &amp; Vital Records</p>
              </div>
            </div>
            <div className="px-6 py-6">
              <p className="text-gray-500 text-sm leading-relaxed mb-4">
                First-time passport applicants need a certified birth certificate. South Carolina birth records can be ordered through SCDHEC Vital Records.
              </p>
              <a href="https://www.scdhec.gov/vitalrecords" target="_blank" rel="noopener noreferrer nofollow" className="inline-flex items-center gap-1.5 text-[#1A365D] hover:text-[#F6C344] font-semibold text-sm transition-colors whitespace-nowrap">
                Order SC Birth Certificate <i className="ri-external-link-line"></i>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}